import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/auth', { replace: true });
      return;
    }

    const exchangeSession = async () => {
      try {
        const res = await axios.get(`${API}/auth/session?session_id=${sessionId}`, { withCredentials: true });
        const data = res.data;
        if (data.token) {
          sessionStorage.setItem('bookr_token', data.token);
          setToken(data.token); // update live context so API calls authenticate immediately
        }
        setUser({
          user_id: data.user_id,
          email: data.email,
          name: data.name,
          avatar_url: data.picture,
          role: data.role
        });
        // Route by role, and honor "Apply as a provider" intent stashed before the
        // Google round-trip (a fresh Google account is a customer until they submit).
        const providerIntent = sessionStorage.getItem('bookr_provider_intent');
        sessionStorage.removeItem('bookr_provider_intent');
        if (data.role === 'admin') navigate('/admin', { replace: true });
        else if (data.role === 'provider') navigate('/provider/dashboard', { replace: true });
        else if (providerIntent) navigate('/provider/setup-profile', { replace: true });
        else navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('Google sign-in exchange failed:', err?.response?.status, err?.message);
        navigate('/auth', { replace: true });
      }
    };

    exchangeSession();
  }, [location, navigate, setUser, setToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-zinc-500 text-sm" data-testid="auth-callback-loading">Signing you in...</p>
      </div>
    </div>
  );
}
